import { err, ok, parseError, type ParseError, type Result } from '../result.ts';

/**
 * `GET` / `POST /api/v1/auth/preferences` — §8's preference sync, measured directly (API reference
 * §8d). **Six findings, and five of them are silent.**
 *
 * ### 1. The `POST` is a whole-object *replace*, not a patch
 *
 * Every field the body omits is reset to the **instance's default**, with a `204` and an empty body
 * either way. Measured: eleven non-default fields were written, then `{"quality":"medium"}` was sent
 * alone — and ten of the eleven came back at their defaults (`captions`, `dark_mode`, `default_home`,
 * `local`, `locale`, `max_results`, `quality_dash`, `related_videos`, `speed`, `thin_mode`, `volume`).
 *
 * The account this was measured on was all-default *except* `locale`, which is what makes the trap so
 * good at hiding: a partial `POST` against a fresh account changes exactly one visible thing (the UI
 * language goes to `en-US`) and looks like a working patch endpoint.
 *
 * So **a write is only expressible as a merge over a fresh read**, and that is enforced by the
 * signature here rather than by a comment: {@link preferencesWriteBody} takes an
 * {@link InstancePreferences}, which can only come from {@link parseInstancePreferences}, which can
 * only come from a `GET`. There is no way to build a body from nothing.
 *
 * ### 2. Unknown keys are ignored, and *that is what makes the merge safe*
 *
 * A body carrying `visious_nonsense` is accepted (`204`, no error, no storage). Combined with (1) it
 * means the merge can be done over the **raw** payload — every key the instance sent goes back
 * verbatim, including the **21** this app has no opinion about (`comments`, `feed_menu`, `sort`,
 * `search_privacy`, `vr_mode`, …) and any key a future Invidious adds. A client that rebuilt the
 * object from its own typed model would silently wipe those.
 *
 * ### 3. One wrong-typed field discards the **entire** write
 *
 * `quality: 7`, `max_results: "abc"`, `local: "yes"`, `captions: [null,"",""]` and — the one that will
 * bite a UI — `volume: 100.5` each answer `204` and change *nothing at all*, including the 35 fields
 * that were correct. A `null` is different: `default_home: null` and `captions: null` are ignored and
 * the rest of the body applies. So {@link preferencesWriteBody} validates its own body against the
 * document it merged into, and refuses rather than sending a write that will evaporate.
 *
 * ### 4. There is no value validation at all
 *
 * `quality: "totally-bogus"`, `quality_dash: "bogus"`, `default_home: "Nonsense"`, `locale: "xx-XX"`,
 * `region: "ZZ"`, `dark_mode: "maybe"` and `feed_menu: ["Nonsense"]` are all stored verbatim and
 * handed back by the next `GET`. Hence R3/R4 on the read side: **every** field here is nullable
 * because any of them can be a value no version of Invidious would produce.
 *
 * The exceptions are two converters and one clamp: `dark_mode: true/false` is stored as
 * `"dark"`/`"light"`, `captions: "English"` becomes an array, and `max_results` is clamped to
 * `0 … 1500`.
 *
 * ### 5. A body the server cannot parse is also a `204`
 *
 * Invalid JSON, a JSON array, a bare string and no body at all each answer `204` and change nothing —
 * the handler falls back to the stored preferences. So **the response cannot distinguish "replaced
 * everything" from "did nothing"**, and a caller that must know re-reads.
 *
 * ### 6. `max_results` written here is observable
 *
 * It sizes the unparameterised window of both authenticated lists: with `max_results: 17`, a bare
 * `GET /api/v1/auth/history` returned 17 rows and a bare `GET /api/v1/auth/feed` returned 15 + 2. That
 * is the one place this project can watch a preference have an effect — and the reason 3b and 3d
 * always send the parameter explicitly.
 */

export const PREFERENCES_PATH = '/api/v1/auth/preferences';

/**
 * The subset of the instance's 35 fields that maps onto something Visious means, parsed defensively.
 *
 * **Every field is nullable, and that is finding 4 rather than caution**: the endpoint stores whatever
 * it is given, so `quality` can be `"totally-bogus"` and `max_results` can be `1500`. `null` means
 * "no value this build can use", which the sync layer reads as "leave the local setting alone".
 */
export interface InstancePreferences {
  /**
   * The payload exactly as it arrived — the merge base for every write (findings 1 and 2).
   *
   * It is on the parsed object rather than kept beside it because the two must never come apart: a
   * write merged into *some other* read is a write that resets whatever changed in between.
   */
  readonly raw: Readonly<Record<string, unknown>>;
  /** `dash` on this instance, meaning "use the adaptive manifest". Free-form (finding 4). */
  readonly quality: string | null;
  /** `auto`, `1080p`, … — the height *within* the DASH ladder. Also free-form. */
  readonly qualityDash: string | null;
  readonly speed: number | null;
  /** 0–100 on the wire, **Int32** — a float discards the whole write (finding 3). */
  readonly volume: number | null;
  /** Language *names* (`"German"`), not codes — Invidious's own caption labels. Three slots. */
  readonly captions: readonly string[];
  /** `""` (follow the browser), `dark`, `light`, or anything at all. */
  readonly darkMode: string | null;
  /** The content region — **not in §8's sync table, and it is the same setting** (3e). */
  readonly region: string | null;
  /** Reset to `en-US` by any body that omits it (finding 1). 4e's, once there are strings to localise. */
  readonly locale: string | null;
  readonly maxResults: number | null;
  /** `Popular` | `Trending` | `Subscriptions` | `Playlists` on this instance. Free-form. */
  readonly defaultHome: string | null;
  readonly autoplay: boolean | null;
  readonly relatedVideos: boolean | null;
  /** Whether the account records watched state at all — 3d's mirroring switch, server-side. */
  readonly watchHistory: boolean | null;
  /** Invidious's resume-position preference. 4b's, and the API stores no timestamps to resume *to*. */
  readonly savePlayerPos: boolean | null;
  /** Proxy media through the instance. **No Visious counterpart** — see the constant below. */
  readonly local: boolean | null;
}

/**
 * Fields the instance stores that Visious deliberately does **not** map, with the reason. Exported so
 * the settings page can say so rather than implying the sync is total.
 *
 * `local` is the interesting one, and it corrects §8's own table: the plan maps it to a "proxy-media
 * toggle", but §2 makes `?local=true` **mandatory** — a direct googlevideo URL can never work from a
 * browser — so there is no toggle for it to be. Syncing the field would offer a switch whose off
 * position breaks playback.
 *
 * `max_results` joined the list when the rows-per-page setting was retired: every grid that
 * used to be paged auto-loads on scroll, so the window size is a request parameter and not a choice
 * anyone was making. Visious sends its own constant on both paged reads and leaves the account's
 * number to the instance's own preferences page.
 */
export const PREFERENCE_FIELDS_WITHOUT_A_SETTING: readonly string[] = [
  'local',
  'max_results',
  'player_style',
  'listen',
  'video_loop',
  'thin_mode',
  'comments',
  'feed_menu',
  'sort',
  'annotations',
  'annotations_subscribed',
  'automatic_instance_redirect',
  'continue',
  'continue_autoplay',
  'extend_desc',
  'latest_only',
  'notifications_only',
  'preload',
  'search_privacy',
  'show_nick',
  'unseen_only',
  'vr_mode',
];

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(raw: Readonly<Record<string, unknown>>, key: string): string | null {
  const value = raw[key];
  return typeof value === 'string' ? value : null;
}

function num(raw: Readonly<Record<string, unknown>>, key: string): number | null {
  const value = raw[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function bool(raw: Readonly<Record<string, unknown>>, key: string): boolean | null {
  const value = raw[key];
  return typeof value === 'boolean' ? value : null;
}

/**
 * `GET /api/v1/auth/preferences` → {@link InstancePreferences}.
 *
 * Fails only when the payload is not a JSON object at all. Every field inside is optional and
 * defensively typed (finding 4), so a preference set this build has never seen still parses — which is
 * what keeps a write from being blocked by a field it does not understand.
 */
export function parseInstancePreferences(raw: unknown): Result<InstancePreferences, ParseError> {
  if (!isRecord(raw)) return err(parseError('preferences', null, 'wrong-type'));

  const captions = raw['captions'];
  return ok({
    raw,
    quality: str(raw, 'quality'),
    qualityDash: str(raw, 'quality_dash'),
    speed: num(raw, 'speed'),
    volume: num(raw, 'volume'),
    // R4 at item level: `["German", null, ""]` keeps `German` rather than dropping the field.
    captions: Array.isArray(captions)
      ? (captions as readonly unknown[]).filter((entry): entry is string => typeof entry === 'string')
      : [],
    darkMode: str(raw, 'dark_mode'),
    region: str(raw, 'region'),
    locale: str(raw, 'locale'),
    maxResults: num(raw, 'max_results'),
    defaultHome: str(raw, 'default_home'),
    autoplay: bool(raw, 'autoplay'),
    relatedVideos: bool(raw, 'related_videos'),
    watchHistory: bool(raw, 'watch_history'),
    savePlayerPos: bool(raw, 'save_player_pos'),
    local: bool(raw, 'local'),
  });
}

/**
 * What a write may change — the overlapping subset, by wire name.
 *
 * Wire names rather than the camel-cased ones above, deliberately: this *is* the body, so a typo is a
 * key the instance silently ignores (finding 2) rather than a compile error, and keeping the shape
 * identical to the payload makes {@link checkPreferencesBody}'s type comparison possible at all.
 */
export interface PreferencesPatch {
  readonly quality?: string;
  readonly quality_dash?: string;
  readonly speed?: number;
  readonly volume?: number;
  readonly captions?: readonly string[];
  readonly dark_mode?: string;
  /**
   * 4e's UI language, and the field the whole merge-over-a-fresh-read rule was discovered on: a
   * one-field body wipes the other 34, and on an otherwise-default account `locale` is the only
   * casualty — so a partial write looked like a working patch endpoint that happened to change the
   * language. Written only as one of the 49 tags the instance's own UI offers (`i18n/`).
   */
  readonly locale?: string;
  readonly region?: string;
  readonly max_results?: number;
  readonly default_home?: string;
  readonly autoplay?: boolean;
  readonly watch_history?: boolean;
  /** 4b's resume switch. A boolean — the endpoint has nowhere to put a *position* (see `sync.ts`). */
  readonly save_player_pos?: boolean;
}

/** The two Int32 fields. A float in either one discards the whole write (finding 3). */
export const PREFERENCE_INT_FIELDS: readonly string[] = ['volume', 'max_results'];

export type PreferencesWriteProblem =
  /** A patch value is a type the endpoint will refuse — the write would evaporate silently. */
  | { readonly kind: 'wrong-type'; readonly field: string; readonly expected: string; readonly got: string }
  /** An Int32 field was given a fraction. Its own arm because the *type* looks right. */
  | { readonly kind: 'not-an-integer'; readonly field: string; readonly value: number };

/**
 * Validate a merged body against the document it was merged into — the guard finding 3 forces.
 *
 * **The document is its own schema.** Every key the `GET` returned came back out of the instance's own
 * serialiser, so its `typeof` is exactly what a `POST` will accept for that key; a merged value whose
 * type differs is one the endpoint will reject by discarding the entire request with a `204`. That is a
 * stronger check than a hand-written field table and it cannot drift, because it is read from the
 * response being replaced.
 *
 * Keys the document does not have are left alone: they are finding 2's ignored-unknowns, harmless.
 */
export function checkPreferencesBody(
  body: Readonly<Record<string, unknown>>,
  current: InstancePreferences,
): readonly PreferencesWriteProblem[] {
  const problems: PreferencesWriteProblem[] = [];

  for (const [field, value] of Object.entries(body)) {
    // `null` is *ignored* by the endpoint rather than refused, so it cannot break a write.
    if (value === null) continue;

    if (PREFERENCE_INT_FIELDS.includes(field) && typeof value === 'number' && !Number.isInteger(value)) {
      problems.push({ kind: 'not-an-integer', field, value });
      continue;
    }

    const reference = current.raw[field];
    if (reference === undefined || reference === null) continue;

    const expected = Array.isArray(reference) ? 'array' : typeof reference;
    const got = Array.isArray(value) ? 'array' : typeof value;
    if (expected !== got) problems.push({ kind: 'wrong-type', field, expected, got });
  }

  return problems;
}

/**
 * The body for `POST /api/v1/auth/preferences`: the whole document back, with the patch applied.
 *
 * The shape of the call is the finding — a merge over a fresh read, validated before it is sent:
 *
 * 1. **Start from `current.raw`**, so every field the instance sent survives, including the 21
 *    this app has no setting for and anything a newer Invidious added (findings 1, 2).
 * 2. **Drop patch entries that are `undefined`** — "not being changed" — while letting an explicit
 *    value through even when it equals the stored one.
 * 3. **Refuse rather than send a body that will be discarded** (finding 3). The alternative is a
 *    `204` that means nothing happened, which no caller can tell from success.
 */
export function preferencesWriteBody(
  current: InstancePreferences,
  patch: PreferencesPatch,
): Result<Readonly<Record<string, unknown>>, readonly PreferencesWriteProblem[]> {
  const body: Record<string, unknown> = { ...current.raw };

  for (const [field, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    body[field] = value;
  }

  const problems = checkPreferencesBody(body, current);
  return problems.length === 0 ? ok(body) : err(problems);
}

